import DOMPurify from "dompurify";
import { ID_PREFIX_BOX } from "../../../../../../common/constants";

// guide.json is untrusted (any ZIP a user drops in): never splice it into HTML raw.
export function escapeHtml(v) {
  return String(v == null ? "" : v).replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]
  );
}

// Only plain CSS colour tokens (#hex, names, rgb()/hsl()) — anything else falls back.
const safeColor = (v, fallback) =>
  typeof v === "string" && /^[#\w(),.%\s-]{1,40}$/.test(v) ? v : fallback;
const safeSize = (v, fallback) => (Number.isFinite(Number(v)) ? Number(v) : fallback);

/**
 * Hotspot sentinel: the recorder stores "no hotspot" as full-viewport 0,0,100,100.
 */
function hasCustomHotspot(bb) {
  if (!bb) return false;
  return !(bb.x === 0 && bb.y === 0 && bb.width === 100 && bb.height === 100);
}

/**
 * Resolve the recorder's per-guide video frame (guide.metadata.videoStyle.frame)
 * into a compact spec EnrichedPlayer consumes, or null for the raw/'default'
 * look. Mirrors the recorder's video-style.ts frame semantics:
 *   'default' → raw video, no crop, no bar (null)
 *   'minimal' → crop top/bottom + slim synthetic browser bar
 *   'hidden'  → crop top/bottom only, no bar
 * cropTop/cropBottom are FRACTIONS (0..0.6) of video height. The crop is gated
 * on the clip's capture surface (step.captureSurface): a TAB capture has no
 * browser chrome / taskbar baked in, a WINDOW capture has no taskbar —
 * cropping those would cut real page content. Missing = legacy monitor clip.
 */
const DEFAULT_CROP_TOP = 8;
const DEFAULT_CROP_BOTTOM = 3;
function cropFrac(pct, fallback) {
  const v = typeof pct === "number" ? pct : fallback;
  return Math.max(0, Math.min(0.6, v / 100));
}
function displayUrl(raw) {
  return (raw || "").replace(/^https?:\/\//, "").replace(/\/+$/, "") || "example.com";
}
function videoFrameSpec(videoStyle, startUrl, captureSurface) {
  const f = videoStyle && videoStyle.frame;
  if (!f || (f.mode !== "minimal" && f.mode !== "hidden")) return null; // 'default'/none → raw
  const hasTopChrome = captureSurface !== "browser";
  const hasTaskbar = captureSurface !== "browser" && captureSurface !== "window";
  return {
    cropTop: hasTopChrome ? cropFrac(f.cropTop, DEFAULT_CROP_TOP) : 0,
    cropBottom: hasTaskbar ? cropFrac(f.cropBottom, DEFAULT_CROP_BOTTOM) : 0,
    bar: f.mode === "minimal", // only 'minimal' overlays a bar
    theme: f.theme === "light" ? "light" : "dark",
    border: !!f.border,
    url: displayUrl(f.url || startUrl || ""),
  };
}

// CSS background from guide.json goes into an inline style — allow only
// gradients / colours (no url(), no quotes, no declarations).
const safeBackground = (v) =>
  typeof v === "string" &&
  v.length <= 400 &&
  /^[#\w(),.%\s-]+$/.test(v) &&
  !/(url|image|element)\s*\(/i.test(v)
    ? v
    : "";

/**
 * Resolve the recorder's wallpaper (guide.metadata.videoStyle background +
 * padding + radius + shadow) or null when it renders borderless. Mirrors
 * video-style.ts videoFrameStyles(): the 'hidden' frame mode strips ALL
 * decoration. padding/radius stay in the recorder's px (EnrichedPlayer scales
 * them against videoZoom.js LOOK_REF_W).
 */
function videoWallpaperSpec(videoStyle) {
  if (!videoStyle) return null;
  if (videoStyle.frame && videoStyle.frame.mode === "hidden") return null;
  const bg = videoStyle.background || {};
  const background = bg.type && bg.type !== "hidden" ? safeBackground(bg.value) : "";
  const padding = Math.max(0, Math.min(200, safeSize(videoStyle.padding, 0)));
  const radius = Math.max(0, Math.min(200, safeSize(videoStyle.radius, 0)));
  const shadow = !!videoStyle.shadow;
  if (!background && !padding && !radius && !shadow) return null;
  return { background, padding, radius, shadow };
}

/**
 * Convert one Step → array of box definitions.
 *
 * @param {object} step - from guide.json
 * @param {object} assetUrls - { screenshots: { [id]: url }, audio: { ['step-'+stepId]: url }, videos: { [videoId]: url } }
 * @param {string} sceneId - parent scene (pa-...) id
 * @param {number} stepOrderIndex - 0-based index, used to make unique box ids within scene
 * @returns {Array} box definitions ready for createBox()
 */
export function stepToBoxes(step, assetUrls, sceneId, stepOrderIndex, opts) {
  const boxes = [];
  const baseTs = Date.now();
  const mkBoxId = (suffix) => ID_PREFIX_BOX + baseTs + "_" + stepOrderIndex + "_" + suffix;
  const stepIdToSceneId = (opts && opts.stepIdToSceneId) || null;
  const videoStyle = (opts && opts.videoStyle) || null;
  const startUrl = (opts && opts.startUrl) || null;
  const remap = (rawTarget) =>
    rawTarget && stepIdToSceneId && stepIdToSceneId[rawTarget] ? stepIdToSceneId[rawTarget] : null;

  // 1. Screenshot background — full-bleed via HotspotImages
  const ssId = step.beforeScreenshotId || step.afterScreenshotId;
  const ssUrl = ssId ? assetUrls.screenshots[ssId] : null;
  if (ssUrl) {
    boxes.push({
      id: mkBoxId("ss"),
      parent: sceneId,
      container: 0,
      page: sceneId,
      position: { x: "0", y: "0", type: "absolute" },
      width: "100%",
      height: "100%",
      name: "HotspotImages",
      url: ssUrl,
      initialState: { url: ssUrl },
    });
  }

  // 2. Build the MultiInput item list. Tag-display annotations and the action's
  //    interactive piece all become items inside one GuideMultiInput.
  const miItems = [];
  let miItemSeq = 0;
  const newItemId = () => "mi_" + baseTs + "_" + stepOrderIndex + "_" + miItemSeq++;

  // 2a. Non-interactive annotations (text / shapes) keep emitting standalone boxes.
  for (const ann of step.annotations || []) {
    if (ann.type === "text") {
      boxes.push({
        id: mkBoxId("ann-" + ann.id),
        parent: sceneId,
        container: 0,
        page: sceneId,
        position: { x: ann.x + "%", y: ann.y + "%", type: "absolute" },
        width: Math.abs(ann.width) + "%",
        height: Math.abs(ann.height) + "%",
        name: "BasicText",
        initialState: {
          __text:
            '<p style="color:' +
            escapeHtml(safeColor(ann.color, "#000")) +
            ";font-size:" +
            safeSize(ann.fontSize, 24) +
            "px;margin:0;" +
            (safeColor(ann.backgroundColor, "")
              ? "background:" + escapeHtml(safeColor(ann.backgroundColor, "")) + ";padding:4px;"
              : "") +
            '">' +
            escapeHtml(ann.text) +
            "</p>",
        },
      });
      continue;
    }
    if (ann.type === "tagDisplay") {
      miItems.push({
        id: newItemId(),
        kind: "tag",
        unit: "%",
        x: ann.x,
        y: ann.y,
        width: Math.abs(ann.width),
        height: Math.abs(ann.height),
        label: "",
        color: "#a855f7",
        tagRef: ann.tagRef || "",
      });
      continue;
    }
    // rectangle / arrow / circle / highlight / blur / hotspot / clickToNavigate
    boxes.push({
      id: mkBoxId("ann-" + ann.id),
      parent: sceneId,
      container: 0,
      page: sceneId,
      position: { x: ann.x + "%", y: ann.y + "%", type: "absolute" },
      width: Math.abs(ann.width) + "%",
      height: Math.abs(ann.height) + "%",
      name: "ShapeOverlay",
      initialState: {
        shape: ann.type,
        color: ann.color || "#ef4444",
        flipH: ann.width < 0,
        flipV: ann.height < 0,
        targetStepId: ann.targetStepId || null,
      },
    });
  }

  // 3. Description — position/visibility mirror the recorder player's
  //    step.tooltipPosition ('left' | 'right' | 'hidden'; default 'left').
  //    'hidden' emits nothing; 'right' anchors the box to the bottom-right,
  //    mirroring the box's 3% left margin (100 - 30 width - 3 = 67).
  const tooltipPosition = step.tooltipPosition || "left";
  if (step.description && step.description.trim() && tooltipPosition !== "hidden") {
    const descX = tooltipPosition === "right" ? "67%" : "3%";
    boxes.push({
      id: mkBoxId("desc"),
      parent: sceneId,
      container: 0,
      page: sceneId,
      position: { x: descX, y: "75%", type: "absolute" },
      width: "30%",
      height: "auto",
      name: "BasicText",
      initialState: {
        __text:
          '<div style="background:rgba(255,255,255,0.95);padding:12px;border-radius:8px;box-shadow:0 4px 12px rgba(0,0,0,0.15);">' +
          DOMPurify.sanitize(step.description, { ADD_ATTR: ["target"] }) +
          "</div>",
      },
    });
  }

  // 4. Interactive overlay → MultiInput items
  const action = step.action || { type: "screenshot" };
  const actionType = action.type;

  if (actionType === "click" || actionType === "dblclick") {
    if (hasCustomHotspot(step.element && step.element.boundingBox)) {
      const bb = step.element.boundingBox;
      miItems.push({
        id: newItemId(),
        kind: "click",
        unit: "%",
        x: bb.x,
        y: bb.y,
        width: bb.width,
        height: bb.height,
        label: "",
        color: "#ff00a0",
        actionType,
        onClickAction: action.onClickAction || "next",
        targetStepId: remap(action.targetStepId),
        messageText: action.messageText || null,
      });
    }
  } else if (actionType === "type") {
    const bb = (step.element && step.element.boundingBox) || {
      x: 40,
      y: 45,
      width: 20,
      height: 5,
    };
    miItems.push({
      id: newItemId(),
      kind: "input",
      unit: "%",
      x: bb.x,
      y: bb.y,
      width: bb.width,
      height: bb.height,
      label: "",
      color: "#3b82f6",
      inputType: action.inputType || "text",
      validation:
        action.value != null && action.value !== ""
          ? { mode: "exact", value: action.value }
          : { mode: "random" },
      successMessage: action.successMessage || null,
      failureMessage: action.failureMessage || null,
      tag: action.tag || null,
      onClickAction: "next",
      targetStepId: null,
    });
  } else if (actionType === "multi-input") {
    const rawItems = Array.isArray(action.items) ? action.items : [];
    for (const it of rawItems) {
      const remapped = remap(it && it.targetStepId);
      miItems.push({
        ...it,
        id: it && it.id ? it.id : newItemId(),
        targetStepId: remapped,
      });
    }
  } else if (actionType === "video") {
    const vId = step.videoId;
    const vUrl = vId && assetUrls.videos[vId];
    if (vUrl) {
      // Auto-zoom camera path baked by the recorder (step.zoomKeyframes) and the
      // per-guide look (videoStyle: wallpaper + browser frame). Both are non-destructive
      // overlays EnrichedPlayer applies at playback — see EnrichedPlayerPlugin
      // (videoZoom.js). Replaces the old generic Ken-Burns Pan preset, which was
      // never even forwarded by createBox and ignored the real click-tracking
      // camera, so imported videos played dead flat.
      const zoomKeyframes = Array.isArray(step.zoomKeyframes) ? step.zoomKeyframes : null;
      const videoFrame = videoFrameSpec(videoStyle, startUrl, step.captureSurface);
      const videoWallpaper = videoWallpaperSpec(videoStyle);
      boxes.push({
        id: mkBoxId("vid"),
        parent: sceneId,
        container: 0,
        page: sceneId,
        position: { x: "10%", y: "10%", type: "absolute" },
        width: "80%",
        height: "60%",
        name: "EnrichedPlayer",
        url: vUrl,
        initialState: {
          url: vUrl,
          zoomKeyframes: zoomKeyframes || undefined,
          videoFrame: videoFrame || undefined,
          videoWallpaper: videoWallpaper || undefined,
        },
      });
    }
  }
  // else: screenshot / navigate / select / scroll / tabSwitch
  //       → screenshot + description only, no interactive overlay

  // 5. Emit a single full-bleed GuideMultiInput if there is at least one item.
  if (miItems.length > 0) {
    boxes.push({
      id: mkBoxId("multi"),
      parent: sceneId,
      container: 0,
      page: sceneId,
      position: { x: "0", y: "0", type: "absolute" },
      width: "100%",
      height: "100%",
      name: "GuideMultiInput",
      initialState: { items: miItems, advanceOnComplete: true },
    });
  }

  return boxes;
}
